import { lazy, Suspense, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ProductAction, ProductSession } from "@matrix-idp/api-contracts";
import {
  ArrowRight,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Files,
  KeyRound,
  LoaderCircle,
  LogOut,
  MailCheck,
  ShieldAlert,
  ShieldX,
  Users
} from "lucide-react";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { LoadingState } from "@/components/ui";
import { Toaster } from "@/components/ui/sonner";
import { apiRequest, OpsApiError } from "@/lib/api";
import {
  beginOpsLogin,
  completeOpsLogin,
  hasOpsSession,
  hasPendingOpsLogin,
  onOpsSessionCleared,
  signOutOps
} from "@/lib/oauth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

const KnowledgePage = lazy(() =>
  import("./pages/KnowledgePage.tsx").then(({ KnowledgePage }) => ({ default: KnowledgePage }))
);
const DocumentsPage = lazy(() =>
  import("./pages/DocumentsPage.tsx").then(({ DocumentsPage }) => ({ default: DocumentsPage }))
);
const AccountManagementPage = lazy(() =>
  import("./pages/AccountManagementPage.tsx").then(({ AccountManagementPage }) => ({
    default: AccountManagementPage
  }))
);
const AutomationPage = lazy(() =>
  import("./pages/AutomationPage.tsx").then(({ AutomationPage }) => ({
    default: AutomationPage
  }))
);
const TrialApplicationsPage = lazy(() =>
  import("./pages/TrialApplicationsPage.tsx").then(({ TrialApplicationsPage }) => ({
    default: TrialApplicationsPage
  }))
);

const navigation = [
  {
    to: "/documents",
    label: "文档",
    shortLabel: "文档",
    icon: Files,
    actions: ["project.manage", "file.workspace.use", "knowledge.query", "knowledge.manage"]
  },
  {
    to: "/automation",
    label: "自动化",
    shortLabel: "自动化",
    icon: CalendarClock,
    actions: ["knowledge.manage"]
  },
  {
    to: "/accounts",
    label: "账号管理",
    shortLabel: "账号管理",
    icon: Users,
    actions: ["account.manage"]
  },
  {
    to: "/waiting-list",
    label: "试用申请",
    shortLabel: "试用申请",
    icon: MailCheck,
    actions: ["account.manage"]
  }
] satisfies Array<{
  to: string;
  label: string;
  shortLabel: string;
  icon: typeof Files;
  actions: ProductAction[];
}>;

function permittedNavigation(session: ProductSession) {
  const allowed = new Set(session.allowedActions);
  return navigation.filter((item) => item.actions.some((action) => allowed.has(action)));
}

function ProductMark({ className }: { className?: string }) {
  return (
    <img
      src="/product-logo.svg"
      alt=""
      aria-hidden="true"
      draggable={false}
      className={cn("size-9 shrink-0 rounded-[10px] shadow-sm", className)}
    />
  );
}

function TopRail({
  onSignOut,
  items,
  session
}: {
  onSignOut(): void;
  items: typeof navigation;
  session: ProductSession;
}) {
  const location = useLocation();
  const current = items.find(
    (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
  );
  const title = sessionAccountTitle(session);
  const roleLabel = sessionRoleLabel(session);
  const initials = sessionAccountInitials(title);

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border bg-background/90 px-4 backdrop-blur md:px-6">
      <SidebarTrigger className="-ml-1" />
      <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:flex">
        智能文档平台
        <ChevronRight className="size-3" />
        <strong className="font-semibold text-foreground">{current?.shortLabel ?? "文档"}</strong>
      </span>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="ml-auto h-9 max-w-[16rem] gap-2 px-2 text-foreground hover:bg-muted hover:text-foreground data-[state=open]:bg-muted"
            aria-label="当前账号菜单"
          >
            <span
              aria-hidden="true"
              className="grid size-7 shrink-0 place-items-center rounded-full bg-primary/10 font-display text-[11px] font-bold text-primary"
            >
              {initials}
            </span>
            <span className="hidden min-w-0 flex-col items-start text-left sm:flex">
              <span className="w-full truncate text-xs font-semibold text-foreground">{title}</span>
              {title !== session.account.email ? (
                <span className="w-full truncate text-[10px] text-muted-foreground">
                  {session.account.email}
                </span>
              ) : roleLabel ? (
                <span className="w-full truncate text-[10px] text-muted-foreground">
                  {roleLabel}
                </span>
              ) : null}
            </span>
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col gap-0.5">
              <span className="truncate text-sm font-semibold text-foreground">{title}</span>
              <span className="truncate text-xs text-muted-foreground">
                {session.account.email}
              </span>
              {roleLabel ? (
                <span className="mt-1 w-fit rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                  {roleLabel}
                </span>
              ) : null}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="focus:bg-muted focus:text-foreground"
            onSelect={() => {
              onSignOut();
            }}
          >
            <LogOut />
            退出登录
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

function AppShell({ onSignOut, session }: { onSignOut(): void; session: ProductSession }) {
  const location = useLocation();
  const items = permittedNavigation(session);
  const isActive = (to: string) =>
    location.pathname === to || location.pathname.startsWith(`${to}/`);

  return (
    <SidebarProvider className="h-svh min-h-0 overflow-hidden">
      <Sidebar>
        <SidebarHeader className="gap-3 border-b border-sidebar-border px-4 py-4">
          <div className="flex items-center gap-3">
            <ProductMark className="ring-1 ring-white/15" />
            <div className="flex min-w-0 flex-col">
              <strong className="font-display text-[15px] font-bold text-sidebar-foreground">
                Matrix IDP
              </strong>
              <span className="truncate text-[10px] text-sidebar-foreground/60">
                智能文档知识平台
              </span>
            </div>
          </div>
        </SidebarHeader>

        <SidebarContent className="px-2 py-3">
          <SidebarGroup>
            <SidebarGroupLabel>管理控制台</SidebarGroupLabel>
            <SidebarMenu>
              {items.map(({ to, label, icon: Icon }) => (
                <SidebarMenuItem key={to}>
                  <SidebarMenuButton asChild isActive={isActive(to)} tooltip={label}>
                    <NavLink to={to}>
                      <Icon />
                      <span>{label}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>

      <SidebarInset className="min-h-0 overflow-hidden">
        <TopRail onSignOut={onSignOut} items={items} session={session} />
        <div className="page-content flex min-h-0 min-w-0 w-full flex-1 flex-col gap-5 overflow-auto px-[2.5%] py-6 md:px-[3%] md:py-7">
          <Suspense fallback={<LoadingState />}>
            <div className="flex min-h-0 flex-1 flex-col">
              <Routes>
                <Route path="/documents/*" element={<DocumentsPage />} />
                <Route path="/knowledge/*" element={<KnowledgePage />} />
                <Route path="/automation" element={<AutomationPage />} />
                <Route path="/accounts" element={<AccountManagementPage />} />
                <Route path="/waiting-list" element={<TrialApplicationsPage />} />
                <Route path="*" element={<Navigate to={items[0]?.to ?? "/"} replace />} />
              </Routes>
            </div>
          </Suspense>
        </div>
        <Toaster />
      </SidebarInset>
    </SidebarProvider>
  );
}

function sessionAccountTitle(session: ProductSession): string {
  const name = session.account.displayName?.trim();
  return name || session.account.email;
}

function sessionRoleLabel(session: ProductSession): string | null {
  const role = session.roleAssignments[0]?.role;
  if (!role) return null;
  switch (role) {
    case "super_administrator":
      return "超级管理员";
    case "platform_operator":
      return "平台操作员";
    default:
      return null;
  }
}

function sessionAccountInitials(title: string): string {
  const value = title.trim();
  if (!value) return "?";
  if (value.includes("@")) {
    return value.slice(0, 1).toUpperCase();
  }
  const parts = value.split(/\s+/u).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
  }
  return value.slice(0, 2).toUpperCase();
}

export function App() {
  return <AuthenticationBoundary />;
}

function isOAuthCallbackPath(): boolean {
  return window.location.pathname === "/oauth/callback";
}

type AuthenticationState =
  "checking" | "completing" | "signed-out" | "authorized" | "forbidden" | "failed";

function AuthenticationBoundary() {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<ProductSession | null>(null);
  const [verificationAttempt, setVerificationAttempt] = useState(0);
  const [state, setState] = useState<AuthenticationState>(() =>
    isOAuthCallbackPath() ? "completing" : "checking"
  );

  useEffect(
    () =>
      onOpsSessionCleared(() => {
        setSession(null);
        queryClient.clear();
        setState("signed-out");
      }),
    [queryClient]
  );

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        if (isOAuthCallbackPath()) {
          if (active) setState("completing");
          const returnTo = await completeOpsLogin();
          if (!active) return;
          window.history.replaceState(null, "", returnTo);
        } else if (!hasOpsSession()) {
          if (active) setState("signed-out");
          return;
        }
        const currentSession = await apiRequest<ProductSession>("/v1/ops/session");
        if (active) setSession(currentSession);
        if (active) setState("authorized");
      } catch (error) {
        if (!active) return;
        if (error instanceof OpsApiError && error.status === 401) {
          setSession(null);
          setState("signed-out");
          return;
        }
        setState(error instanceof OpsApiError && error.status === 403 ? "forbidden" : "failed");
      }
    })();
    return () => {
      active = false;
    };
  }, [verificationAttempt]);

  if (state === "authorized" && session) {
    return (
      <AppShell
        onSignOut={() => {
          void signOutOps().finally(() => setState("signed-out"));
        }}
        session={session}
      />
    );
  }

  if (state === "completing") {
    return (
      <AccessState
        variant="loading"
        eyebrow="OAuth 回调"
        title="正在完成登录"
        detail="正在验证授权结果并建立管理会话…"
      />
    );
  }

  if (state === "checking") {
    return (
      <AccessState
        variant="loading"
        eyebrow="会话"
        title="正在验证会话"
        detail="请稍候，正在确认你的登录状态…"
      />
    );
  }

  if (state === "forbidden") {
    return (
      <AccessState
        variant="forbidden"
        eyebrow="权限"
        title="没有管理权限"
        detail="当前账号没有可进入的产品角色。请联系管理员分配产品角色。"
        primaryAction={{
          label: "换账号登录",
          onClick: () => {
            void signOutOps().finally(() => {
              void beginOpsLogin();
            });
          }
        }}
      />
    );
  }

  if (state === "failed") {
    const callbackPending = isOAuthCallbackPath() && hasPendingOpsLogin();
    const callbackExpired = isOAuthCallbackPath() && !callbackPending;
    return (
      <AccessState
        variant="failed"
        eyebrow={callbackExpired ? "登录未完成" : "服务不可用"}
        title={callbackExpired ? "这次登录没有完成" : "会话验证失败"}
        detail={
          callbackExpired
            ? "授权结果已失效或无法在这个浏览器继续。请重新开始登录。"
            : callbackPending
              ? "授权服务暂时不可用。这次登录还没有完成，请稍后重试验证。"
              : "平台或身份服务暂时不可用。当前登录信息会保留，请稍后重试验证。"
        }
        primaryAction={{
          label: callbackExpired ? "重新登录" : "重试验证",
          onClick: () => {
            if (callbackExpired) {
              void beginOpsLogin();
              return;
            }
            setState(isOAuthCallbackPath() ? "completing" : "checking");
            setVerificationAttempt((attempt) => attempt + 1);
          }
        }}
      />
    );
  }

  return (
    <AccessState
      variant="sign-in"
      eyebrow="Ops Console"
      title="登录管理控制台"
      detail="使用受邀账号或企业身份继续，访问文档、知识库与账号管理。"
      primaryAction={{
        label: "继续登录",
        onClick: () => void beginOpsLogin()
      }}
    />
  );
}

type AccessVariant = "loading" | "sign-in" | "failed" | "forbidden";

function AccessState({
  variant,
  eyebrow,
  title,
  detail,
  primaryAction
}: {
  variant: AccessVariant;
  eyebrow: string;
  title: string;
  detail?: string;
  primaryAction?: { label: string; onClick: () => void };
}) {
  const iconWell = (
    <div
      className={cn(
        "mb-5 grid size-14 place-items-center rounded-2xl",
        variant === "failed" || variant === "forbidden"
          ? "bg-destructive/10 text-destructive"
          : "bg-primary/10 text-primary"
      )}
      aria-hidden="true"
    >
      {variant === "loading" ? (
        <LoaderCircle className="size-7 animate-spin" />
      ) : variant === "forbidden" ? (
        <ShieldX className="size-7" />
      ) : variant === "failed" ? (
        <ShieldAlert className="size-7" />
      ) : (
        <KeyRound className="size-7" />
      )}
    </div>
  );

  return (
    <main
      className="access-surface relative grid min-h-svh place-items-center overflow-hidden p-6"
      role={variant === "failed" || variant === "forbidden" ? "alert" : "status"}
      aria-live="polite"
    >
      <section className="access-panel relative w-full max-w-[400px] overflow-hidden rounded-[calc(var(--radius)+2px)] border border-border/90">
        <div className="flex items-center gap-3 border-b border-border/80 bg-muted/50 px-6 py-3.5">
          <ProductMark />
          <div className="flex min-w-0 flex-col">
            <strong className="font-display text-[13px] font-bold text-foreground">
              Matrix IDP
            </strong>
            <span className="truncate text-[11px] text-muted-foreground">管理控制台</span>
          </div>
        </div>

        <div className="px-7 py-8">
          {iconWell}
          <p className="mb-2 text-[11px] font-semibold text-primary">{eyebrow}</p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-foreground">
            {title}
          </h1>
          {detail ? (
            <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">{detail}</p>
          ) : null}

          {primaryAction ? (
            <Button
              type="button"
              className="mt-7 h-11 w-full gap-2 text-sm font-semibold"
              onClick={primaryAction.onClick}
            >
              <KeyRound className="size-4" />
              {primaryAction.label}
              <ArrowRight className="ml-auto size-4" />
            </Button>
          ) : null}
        </div>

        <div className="border-t border-border bg-muted/55 px-6 py-3 text-[11px] leading-relaxed text-muted-foreground">
          {variant === "forbidden"
            ? "当前账号没有可用的产品角色。"
            : variant === "failed"
              ? "若问题持续，请联系管理员检查身份服务。"
              : "仅受邀账号可访问。登录经安全身份入口完成。"}
        </div>
      </section>
    </main>
  );
}
